import React, { useState } from 'react';

const SIZES = [16, 20, 24, 32, 48, 64];
const COLORS = ['#4D4D4D', '#1F2937', '#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'];

const AtomIcon = ({ size = 24, color = '#4D4D4D' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.5"/>
        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke={color} strokeWidth="1.2"/>
        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke={color} strokeWidth="1.2"/>
        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke={color} strokeWidth="1.2"/>
    </svg>
);

const AbstractNIcon = ({ size = 24, color = '#4D4D4D' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5"/>
        <path d="M8 17V7L16 17V7" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const Section = ({ title, children }) => (
    <div className="mb-12">
        <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">{title}</h2>
        {children}
    </div>
);

const Row = ({ label, children }) => (
    <div className="flex items-center gap-6 mb-6">
        <span className="text-sm text-gray-500 w-32 shrink-0">{label}</span>
        <div className="flex items-center gap-6">{children}</div>
    </div>
);

const LoadingCard = ({ title, children }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center gap-3 min-w-[140px]">
        {children}
        <span className="text-xs text-gray-400">{title}</span>
    </div>
);

const ChatPreview = ({ icon, loading }) => (
    <div className="bg-white rounded-xl border border-gray-200 max-w-sm">
        <div className="border-b px-4 py-3 flex items-center gap-2">
            {icon}
            <span className="font-medium text-sm">Trợ lý Nebula</span>
        </div>
        <div className="px-4 py-3 border-b">
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700 max-w-[80%]">
                Giá HPG hôm nay thế nào?
            </div>
        </div>
        <div className="px-4 py-4">{loading}</div>
    </div>
);

export default function KitDemo() {
    const [activeTab, setActiveTab] = useState('atom');

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <style>{`
                /* === SPIN === */
                @keyframes kit-spin { to { transform: rotate(360deg); } }
                .kit-spin { animation: kit-spin 2s linear infinite; }
                .kit-spin-slow { animation: kit-spin 3s linear infinite; }

                /* === 1. Orbit Pulse (Atom orbits glow sequentially) === */
                @keyframes kit-orbit-1 { 0%,100% { opacity:0.9; stroke-width:1.4; } 33% { opacity:0.3; stroke-width:1; } }
                @keyframes kit-orbit-2 { 0%,100% { opacity:0.3; stroke-width:1; } 33% { opacity:0.9; stroke-width:1.4; } 66% { opacity:0.3; } }
                @keyframes kit-orbit-3 { 0%,66% { opacity:0.3; stroke-width:1; } 66%,100% { opacity:0.9; stroke-width:1.4; } }
                .kit-orbit-1 { animation: kit-orbit-1 1.8s ease-in-out infinite; }
                .kit-orbit-2 { animation: kit-orbit-2 1.8s ease-in-out infinite; }
                .kit-orbit-3 { animation: kit-orbit-3 1.8s ease-in-out infinite; }

                /* === 2. Zoom Ripple (concentric circles scale out + fade) === */
                @keyframes kit-ripple {
                    0% { transform: scale(0.3); opacity: 0.8; }
                    100% { transform: scale(1); opacity: 0; }
                }
                .kit-ripple-1 { animation: kit-ripple 2s ease-out infinite; }
                .kit-ripple-2 { animation: kit-ripple 2s ease-out infinite 0.5s; }
                .kit-ripple-3 { animation: kit-ripple 2s ease-out infinite 1s; }

                /* === 3. Pulse Glow (Claude-style soft throb) === */
                @keyframes kit-glow {
                    0%, 100% { opacity: 0.4; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.15); }
                }
                .kit-glow { animation: kit-glow 1.5s ease-in-out infinite; }

                /* === 4. Orbit Dots (Copilot-style dots circling) === */
                @keyframes kit-dot-orbit {
                    from { transform: rotate(0deg) translateX(10px) rotate(0deg); }
                    to { transform: rotate(360deg) translateX(10px) rotate(-360deg); }
                }
                .kit-dot-1 { animation: kit-dot-orbit 1.5s linear infinite; }
                .kit-dot-2 { animation: kit-dot-orbit 1.5s linear infinite 0.5s; }
                .kit-dot-3 { animation: kit-dot-orbit 1.5s linear infinite 1s; }

                /* === 5. Dash Spin (dashed ring rotating) === */
                @keyframes kit-dash-spin {
                    to { stroke-dashoffset: -31.4; }
                }
                .kit-dash-spin { animation: kit-dash-spin 1s linear infinite; }

                /* === 6. Breathing (scale + opacity gentle cycle) === */
                @keyframes kit-breathe {
                    0%, 100% { transform: scale(0.85); opacity: 0.5; }
                    50% { transform: scale(1.1); opacity: 1; }
                }
                .kit-breathe { animation: kit-breathe 2s ease-in-out infinite; }

                /* === 7. Wave Bars === */
                @keyframes kit-wave {
                    0%, 100% { transform: scaleY(0.4); }
                    50% { transform: scaleY(1); }
                }

                /* === 8. N Draw === */
                @keyframes kit-n-draw {
                    0% { stroke-dashoffset: 40; }
                    50% { stroke-dashoffset: 0; }
                    100% { stroke-dashoffset: -40; }
                }
                .kit-n-draw { stroke-dasharray: 40; animation: kit-n-draw 2s ease-in-out infinite; }

                /* transform origin for SVG */
                .kit-origin-center { transform-origin: 12px 12px; }
            `}</style>

            <div className="max-w-5xl mx-auto">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Nebula Icon Kit</h1>
                <p className="text-gray-500 mb-8">Logo, loading & animation variants</p>

                <div className="flex gap-2 mb-8">
                    {['atom', 'n'].map(tab => (
                        <button key={tab}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab === 'atom' ? 'Atom / Orbital' : 'Abstract N'}
                        </button>
                    ))}
                </div>

                {activeTab === 'atom' ? (
                    <div>
                        <Section title="Static">
                            <Row label="Sizes">
                                {SIZES.map(s => (
                                    <div key={s} className="flex flex-col items-center gap-1">
                                        <AtomIcon size={s} />
                                        <span className="text-xs text-gray-400">{s}</span>
                                    </div>
                                ))}
                            </Row>
                            <Row label="Colors">
                                {COLORS.map(c => (
                                    <div key={c} className="flex flex-col items-center gap-1">
                                        <AtomIcon size={32} color={c} />
                                        <span className="text-xs text-gray-400">{c}</span>
                                    </div>
                                ))}
                            </Row>
                        </Section>

                        <Section title="Loading Animations">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <LoadingCard title="1. Orbit Pulse">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="3" stroke="#4D4D4D" strokeWidth="1.5" className="kit-glow kit-origin-center"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#4D4D4D" strokeWidth="1.2" className="kit-orbit-1"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#4D4D4D" strokeWidth="1.2" className="kit-orbit-2"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#4D4D4D" strokeWidth="1.2" className="kit-orbit-3"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="2. Zoom Ripple">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1" className="kit-ripple-1 kit-origin-center"/>
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="0.8" className="kit-ripple-2 kit-origin-center"/>
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="0.6" className="kit-ripple-3 kit-origin-center"/>
                                        <circle cx="12" cy="12" r="3" stroke="#4D4D4D" strokeWidth="1.5"/>
                                        <ellipse cx="12" cy="12" rx="6" ry="2.5" stroke="#4D4D4D" strokeWidth="0.8"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="3. Pulse Glow">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="3" fill="#4D4D4D" className="kit-glow kit-origin-center"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#4D4D4D" strokeWidth="1" opacity="0.3"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#4D4D4D" strokeWidth="1" opacity="0.3"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#4D4D4D" strokeWidth="1" opacity="0.3"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="4. Orbit Dots">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#4D4D4D" strokeWidth="0.8" opacity="0.2"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#4D4D4D" strokeWidth="0.8" opacity="0.2"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#4D4D4D" strokeWidth="0.8" opacity="0.2"/>
                                        <circle cx="12" cy="12" r="1.5" fill="#4D4D4D" className="kit-dot-1 kit-origin-center"/>
                                        <circle cx="12" cy="12" r="1.2" fill="#4D4D4D" opacity="0.6" className="kit-dot-2 kit-origin-center"/>
                                        <circle cx="12" cy="12" r="1" fill="#4D4D4D" opacity="0.3" className="kit-dot-3 kit-origin-center"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="5. Dash Spin">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="3" stroke="#4D4D4D" strokeWidth="1.5"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#4D4D4D" strokeWidth="1.5" strokeDasharray="5 10" className="kit-spin-slow kit-origin-center"/>
                                        <ellipse cx="12" cy="12" rx="7" ry="3" transform="rotate(90 12 12)" stroke="#4D4D4D" strokeWidth="1" strokeDasharray="4 8" className="kit-spin kit-origin-center" style={{animationDirection:'reverse'}}/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="6. Breathing">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="kit-breathe">
                                        <circle cx="12" cy="12" r="3" stroke="#4D4D4D" strokeWidth="1.5"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#4D4D4D" strokeWidth="1.2"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#4D4D4D" strokeWidth="1.2"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#4D4D4D" strokeWidth="1.2"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="7. Spin + Glow">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="kit-spin-slow kit-origin-center">
                                        <circle cx="12" cy="12" r="3" fill="#4D4D4D" className="kit-glow kit-origin-center"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#4D4D4D" strokeWidth="1.2" strokeDasharray="8 4"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#4D4D4D" strokeWidth="1.2" opacity="0.5" strokeDasharray="8 4"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#4D4D4D" strokeWidth="1.2" opacity="0.25" strokeDasharray="8 4"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="8. Wave Bars">
                                    <div className="flex items-end gap-[3px] h-[48px]">
                                        {[0, 0.15, 0.3, 0.45, 0.3, 0.15, 0].map((d, i) => (
                                            <div key={i} className="w-[4px] bg-gray-500 rounded-full" style={{
                                                height: '24px',
                                                animation: 'kit-wave 1s ease-in-out infinite',
                                                animationDelay: `${d}s`,
                                            }}/>
                                        ))}
                                    </div>
                                </LoadingCard>
                            </div>
                        </Section>

                        <Section title="Chat Loading Previews">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ChatPreview
                                    icon={<AtomIcon size={20} />}
                                    loading={
                                        <div className="flex items-center gap-3">
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="3" stroke="#6B7280" strokeWidth="1.5" className="kit-glow kit-origin-center"/>
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#6B7280" strokeWidth="1.2" className="kit-orbit-1"/>
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#6B7280" strokeWidth="1.2" className="kit-orbit-2"/>
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#6B7280" strokeWidth="1.2" className="kit-orbit-3"/>
                                            </svg>
                                            <span className="text-sm text-gray-500">Orbit Pulse</span>
                                        </div>
                                    }
                                />
                                <ChatPreview
                                    icon={<AtomIcon size={20} />}
                                    loading={
                                        <div className="flex items-center gap-3">
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke="#6B7280" strokeWidth="1" className="kit-ripple-1 kit-origin-center"/>
                                                <circle cx="12" cy="12" r="10" stroke="#6B7280" strokeWidth="0.8" className="kit-ripple-2 kit-origin-center"/>
                                                <circle cx="12" cy="12" r="10" stroke="#6B7280" strokeWidth="0.6" className="kit-ripple-3 kit-origin-center"/>
                                                <circle cx="12" cy="12" r="3" stroke="#6B7280" strokeWidth="1.5"/>
                                            </svg>
                                            <span className="text-sm text-gray-500">Zoom Ripple</span>
                                        </div>
                                    }
                                />
                                <ChatPreview
                                    icon={<AtomIcon size={20} />}
                                    loading={
                                        <div className="flex items-center gap-3">
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#6B7280" strokeWidth="0.8" opacity="0.2"/>
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#6B7280" strokeWidth="0.8" opacity="0.2"/>
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#6B7280" strokeWidth="0.8" opacity="0.2"/>
                                                <circle cx="12" cy="12" r="1.5" fill="#6B7280" className="kit-dot-1 kit-origin-center"/>
                                                <circle cx="12" cy="12" r="1.2" fill="#6B7280" opacity="0.6" className="kit-dot-2 kit-origin-center"/>
                                                <circle cx="12" cy="12" r="1" fill="#6B7280" opacity="0.3" className="kit-dot-3 kit-origin-center"/>
                                            </svg>
                                            <span className="text-sm text-gray-500">Orbit Dots</span>
                                        </div>
                                    }
                                />
                                <ChatPreview
                                    icon={<AtomIcon size={20} />}
                                    loading={
                                        <div className="flex items-center gap-3">
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="kit-breathe">
                                                <circle cx="12" cy="12" r="3" stroke="#6B7280" strokeWidth="1.5"/>
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#6B7280" strokeWidth="1.2"/>
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#6B7280" strokeWidth="1.2"/>
                                                <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#6B7280" strokeWidth="1.2"/>
                                            </svg>
                                            <span className="text-sm text-gray-500">Breathing</span>
                                        </div>
                                    }
                                />
                            </div>
                        </Section>

                        <Section title="Sidebar Preview">
                            <div className="bg-white rounded-xl border border-gray-200 w-64 p-2 space-y-1">
                                <button className="flex items-center gap-3 w-full px-2 py-1.5 rounded-md text-sm text-gray-800 hover:bg-gray-50">
                                    <AtomIcon size={18} /><span>Trợ lý Nebula</span>
                                </button>
                                <button className="flex items-center gap-3 w-full px-2 py-1.5 rounded-md text-sm bg-gray-100 text-gray-900">
                                    <AtomIcon size={18} /><span>Trợ lý Nebula</span>
                                    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">3</span>
                                </button>
                            </div>
                        </Section>

                        <Section title="Dark Mode">
                            <div className="bg-gray-900 rounded-xl p-6">
                                <div className="flex items-center gap-8 mb-6">
                                    <AtomIcon size={32} color="#E5E7EB" />
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="3" stroke="#E5E7EB" strokeWidth="1.5" className="kit-glow kit-origin-center"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#E5E7EB" strokeWidth="1.2" className="kit-orbit-1"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#E5E7EB" strokeWidth="1.2" className="kit-orbit-2"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#E5E7EB" strokeWidth="1.2" className="kit-orbit-3"/>
                                    </svg>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="kit-breathe">
                                        <circle cx="12" cy="12" r="3" stroke="#9CA3AF" strokeWidth="1.5"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" stroke="#9CA3AF" strokeWidth="1.2"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" stroke="#9CA3AF" strokeWidth="1.2"/>
                                        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" stroke="#9CA3AF" strokeWidth="1.2"/>
                                    </svg>
                                </div>
                                <div className="bg-gray-800 rounded-lg px-4 py-2 inline-flex items-center gap-3">
                                    <AtomIcon size={18} color="#D1D5DB" />
                                    <span className="text-sm text-gray-300">Trợ lý Nebula</span>
                                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">5</span>
                                </div>
                            </div>
                        </Section>
                    </div>
                ) : (
                    <div>
                        <Section title="Static">
                            <Row label="Sizes">
                                {SIZES.map(s => (
                                    <div key={s} className="flex flex-col items-center gap-1">
                                        <AbstractNIcon size={s} />
                                        <span className="text-xs text-gray-400">{s}</span>
                                    </div>
                                ))}
                            </Row>
                            <Row label="Colors">
                                {COLORS.map(c => (
                                    <div key={c} className="flex flex-col items-center gap-1">
                                        <AbstractNIcon size={32} color={c} />
                                        <span className="text-xs text-gray-400">{c}</span>
                                    </div>
                                ))}
                            </Row>
                        </Section>

                        <Section title="Loading Animations">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <LoadingCard title="1. N Draw">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1.5"/>
                                        <path d="M8 17V7L16 17V7" stroke="#4D4D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="kit-n-draw"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="2. N Spin">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="kit-spin-slow kit-origin-center">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1.5" strokeDasharray="8 4"/>
                                        <path d="M8 17V7L16 17V7" stroke="#4D4D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="3. N Breathe">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="kit-breathe">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1.5"/>
                                        <path d="M8 17V7L16 17V7" stroke="#4D4D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="4. N Ripple">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1" className="kit-ripple-1 kit-origin-center"/>
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="0.8" className="kit-ripple-2 kit-origin-center"/>
                                        <path d="M8 17V7L16 17V7" stroke="#4D4D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="5. N Glow">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1.5" opacity="0.3"/>
                                        <path d="M8 17V7L16 17V7" stroke="#4D4D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="kit-glow kit-origin-center"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="6. N Zoom">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1" className="kit-ripple-1 kit-origin-center"/>
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="0.8" className="kit-ripple-2 kit-origin-center"/>
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="0.6" className="kit-ripple-3 kit-origin-center"/>
                                        <path d="M8 17V7L16 17V7" stroke="#4D4D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="kit-glow kit-origin-center"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="7. N Dash">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1.5" strokeDasharray="5 10" className="kit-spin-slow kit-origin-center"/>
                                        <circle cx="12" cy="12" r="7" stroke="#4D4D4D" strokeWidth="1" strokeDasharray="4 8" className="kit-spin kit-origin-center" style={{animationDirection:'reverse'}}/>
                                        <path d="M8 17V7L16 17V7" stroke="#4D4D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </LoadingCard>

                                <LoadingCard title="8. N Draw + Glow">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="#4D4D4D" strokeWidth="1.5" className="kit-glow kit-origin-center"/>
                                        <path d="M8 17V7L16 17V7" stroke="#4D4D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="kit-n-draw"/>
                                    </svg>
                                </LoadingCard>
                            </div>
                        </Section>

                        <Section title="Chat Loading Previews">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ChatPreview
                                    icon={<AbstractNIcon size={20} />}
                                    loading={
                                        <div className="flex items-center gap-3">
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke="#6B7280" strokeWidth="1.5"/>
                                                <path d="M8 17V7L16 17V7" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="kit-n-draw"/>
                                            </svg>
                                            <span className="text-sm text-gray-500">N Draw</span>
                                        </div>
                                    }
                                />
                                <ChatPreview
                                    icon={<AbstractNIcon size={20} />}
                                    loading={
                                        <div className="flex items-center gap-3">
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="kit-breathe">
                                                <circle cx="12" cy="12" r="10" stroke="#6B7280" strokeWidth="1.5"/>
                                                <path d="M8 17V7L16 17V7" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span className="text-sm text-gray-500">N Breathe</span>
                                        </div>
                                    }
                                />
                            </div>
                        </Section>

                        <Section title="Dark Mode">
                            <div className="bg-gray-900 rounded-xl p-6 flex items-center gap-8">
                                <AbstractNIcon size={32} color="#E5E7EB" />
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="1.5"/>
                                    <path d="M8 17V7L16 17V7" stroke="#E5E7EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="kit-n-draw"/>
                                </svg>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="kit-breathe">
                                    <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="1.5"/>
                                    <path d="M8 17V7L16 17V7" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </Section>
                    </div>
                )}
            </div>
        </div>
    );
}
